import { supabase } from '../Supabase/SupabaseClient';
import { getPublicUrl } from '../config/supabaseImages';

const WEBP_MIME = 'image/webp';
const DEFAULT_MAX_EDGE = 2000;
const DEFAULT_QUALITY = 0.82;

function supportsWebpEncoding() {
  if (typeof document === 'undefined') return false;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL(WEBP_MIME).startsWith(`data:${WEBP_MIME}`);
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('La imagen no se pudo decodificar'));
    img.src = src;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob !== 'function') {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => resolve(blob), WEBP_MIME, quality);
  });
}

function safeBaseName(name) {
  const base = String(name || 'foto').replace(/\.[^.]+$/, '');
  const clean = base.replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
  return clean || 'foto';
}

export async function convertToWebp(file, options = {}) {
  const { maxEdge = DEFAULT_MAX_EDGE, quality = DEFAULT_QUALITY } = options;

  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('El archivo seleccionado no es una imagen.');
  }
  if (file.type === 'image/svg+xml') {
    throw new Error('Los archivos SVG no se pueden convertir a WebP.');
  }
  if (!supportsWebpEncoding()) {
    throw new Error('Este navegador no permite convertir imágenes a WebP.');
  }

  const dataUrl = await readAsDataUrl(file);
  const img = await loadImageElement(dataUrl);

  const sourceWidth = img.naturalWidth || img.width;
  const sourceHeight = img.naturalHeight || img.height;
  if (!sourceWidth || !sourceHeight) {
    throw new Error('La imagen no tiene dimensiones válidas.');
  }

  const scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('No se pudo preparar el lienzo para convertir la imagen.');
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await canvasToBlob(canvas, quality);
  if (!blob || blob.type !== WEBP_MIME) {
    throw new Error('No se pudo convertir la imagen a WebP.');
  }

  return {
    blob,
    fileName: `${safeBaseName(file.name)}.webp`,
    contentType: WEBP_MIME,
    width,
    height,
    originalBytes: file.size,
    bytes: blob.size,
  };
}

export async function uploadImageToBucket(bucket, path, file) {
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;
  return getPublicUrl(bucket, path);
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
